<script>

    import { onDestroy, onMount, tick } from "svelte";
    import { get } from "svelte/store";
    
    import {
        subscribeSelectedDeviceTrack,
        subscribeSelectedDeviceMuted,
        subscribeSelectedDeviceSelection,
        getSelectedDevice,
        getSelectedDeviceTrack,
        audio_suspended,
        has_joined,
        video_suspended
    } from "../../stores.js";

    import PeerControls from "./PeerControls.svelte"
    import { BuildSettings, OutputConfig } from "../../OutputConfig.js";
    import { Peers_Handler } from "../../Peers.js";
    import { Conference } from "../../Conference/Conference.js";
    import { BrowserHandler } from "../../BrowserHandler.js";
    import { Dispatcher } from "../../Dispatcher.js";
    import { Logger } from "../../Logger.js";

    export let peer;
    export let is_local_tile;

    let name = "";

    let audio;
    let canvas;       // client/eng: main-thread canvas for jitsi video
    let canvasCtx;
    let dts_canvas;   // talent: offscreen-transferred canvas for DTS stream

    let mic_muted;

    let controls_rendered = false;

    let resizeObserver;
    let offscreenVideo = null;
    let rafId = null;
    let currentSinkId = null;
    let currentAudioTrack = null;
    let currentVideoTrack = null;

    // -- Conference monitor gating: self and talent-transport tiles stay muted always; every
    // -- other (remote conference) tile stays muted until the user's first Join. Tiles that mount
    // -- after Join read has_joined === true and come up unmuted on their own.
    $: audio_muted = is_local_tile
        || (peer.type === "talent" && BuildSettings.renderTalentTransport)
        || !$has_joined;

    // -- once per mount: confirms the remote-audio chain went fully AUDIBLE (element unmuted
    // -- with a live track attached). "Tile Audio Attached" fires pre-Join with
    // -- element_muted=true and the Join flip runs no component JS, so without this reactive
    // -- statement there is zero evidence the user ever actually heard anyone.
    let tile_audible_logged = false;
    $: maybeLogAudible(audio_muted, currentAudioTrack);
    function maybeLogAudible(muted, track){
        if (tile_audible_logged || is_local_tile || muted || !track) return;
        tile_audible_logged = true;
        Logger.log("Tile", "info", "Tile Audio Unmuted", {peer_id: peer?.id, track_muted: isTrackMuted(track)});
    }

    // -- the Join flip (has_joined -> audio_muted false) reaches the element through the muted
    // -- binding and runs no other component code, so it is also a moment to confirm the element
    // -- is really playing and not still paused from an earlier audio interruption.
    // -- Deliberately an EDGE on audio_muted alone: depending on currentAudioTrack too would
    // -- re-fire on every attach (the attach assigns it, which dirties this statement) and start
    // -- a second play chain in the same tick, splitting one retry budget across two.
    let was_audio_muted = true;
    $: onAudioMutedChange(audio_muted);

    function onAudioMutedChange(muted){

        const became_audible = was_audio_muted && !muted;
        was_audio_muted = muted;

        if (!became_audible)
            return;

        // -- deferred one tick so this runs AFTER svelte flushes muted={audio_muted} onto the
        // -- element. Reactive statements run before the fragment update, so without the wait we
        // -- would only ever inspect the still-muted element -- and a pause caused by the audible
        // -- transition itself is exactly what this call exists to catch.
        // -- restart=true: this edge can fire at most once per page load (has_joined is one-shot
        // -- and never goes back to false), so a call swallowed by the retry guard is lost for
        // -- the whole session, unlike an attach, which comes round again.
        tick().then(() => ensureAudioPlaying("unmuted", true));
    }
    
    // --- Canvas-based video rendering for client/eng peers ---
    // -- Deliberately a canvas pump, NOT a <video> tag: compositing a MediaStream video element
    // -- re-paces the display link to the camera's ~30fps, which drops the decoder worker's
    // -- playback RAF with it and ruins the 23.976fps pulldown cadence. The hidden pump video
    // -- below never enters the compositor, so the display stays at full rate.

    function getMediaStreamTrack(track) {
        return track?.getTrack?.() ?? track;
    }

    function isTrackMuted(track) {
        return track?.isMuted?.() ?? track?.muted ?? false;
    }

    // -- INTENTIONAL mute only -- the one that should blank a tile. For a remote tile that is
    // -- JitsiRemoteTrack.isMuted() (the signaled presence flag: the peer turned their camera
    // -- off). For the local tile the track is a raw MediaStreamTrack, whose .muted is the
    // -- OS-driven flag (page backgrounded, mic/camera interrupted by a call, another app took
    // -- the device) -- NOT a user intent, and deliberately ignored here: the user's own camera
    // -- mute already arrives via the selected-device muted store. Reading .muted to gate the
    // -- pump is what left the local tile dark after every iOS background cycle: WebKit updates
    // -- the JS-visible muted flag in an event-loop task queued AFTER visibilitychange, so the
    // -- resume path always sampled muted===true, stopped the pump, and nothing listened for the
    // -- 'unmute' that landed one task later.
    // -- What happens now on that resume: a pump IS built over the still-muted track. The tile
    // -- is blank (not stale -- startVideoProcessor clears the canvas first) and pump() bails on
    // -- readyState < 2 for as long as no frame has arrived; note the frame counter is pinned at
    // -- 0 for a detached element on WebKit, so readyState is the gate that matters here. It
    // -- recovers with no further help from us because the WebKit player attaches its frame
    // -- observer to the capture SOURCE regardless of mute state: when the source resumes, the
    // -- first frame drives readyState to HaveEnoughData and the intrinsic size lands with it.
    function isUserMuted(track) {
        return track?.isMuted?.() ?? false;
    }

    // -- a track that has ENDED (camera died / stopped by a jitsi mute) can never produce a
    // -- frame, so don't build a pump over it -- the element would park below readyState 2 and
    // -- hold whatever was on the canvas. Repairing an ended track is Devices' job.
    function isTrackEnded(track) {
        return getMediaStreamTrack(track)?.readyState === "ended";
    }

    function detachAudioTrack(track) {
        if (!track || !audio)
            return;

        if (track.detach)
            track.detach(audio);
        else if (audio.srcObject)
            audio.srcObject = null;
    }

    function attachAudioTrack(track) {
        if (!audio || !track)
            return;

        if (track.attach) {
            track.attach(audio);
            return;
        }

        const mediaTrack = getMediaStreamTrack(track);
        if (mediaTrack)
            audio.srcObject = new MediaStream([mediaTrack]);
    }

    // -- The element carries `autoplay`, but that only covers its FIRST start. A system audio
    // -- interruption on iOS (call banner, Siri, an alarm, another app taking the session)
    // -- pauses every media element on the page, and WebKit only re-plays them when iOS supplies
    // -- the ShouldResume hint -- without it they stay paused for good. Nothing else ever calls
    // -- play(): lib-jitsi-meet's attach() just assigns srcObject. So a remote peer's voice was
    // -- silently gone until their track happened to be re-attached, or the page was reloaded.
    // -- (This is what jitsi-meet's own AudioTrack does after every attach, retries included.)
    let audio_play_timer = null;
    let audio_play_attempts = 0;
    let audio_play_failures = 0;   // -- per-tile log cap, like pump_play_failures below

    function ensureAudioPlaying(reason, restart = false){

        if (!audio || !audio.srcObject)
            return;

        // -- a retry is already armed: let that chain finish instead of resetting its budget.
        // -- Track stores re-notify even for an unchanged track object (svelte's safe_not_equal
        // -- never dedupes objects), so a churn of identical attaches would otherwise keep the
        // -- counter below the cap and the failure would never be logged. `restart` marks the
        // -- callers that legitimately supersede a sleeping chain -- the one-shot Join unmute,
        // -- which never comes round again. Gating on a TIMER is safe (it always fires or is
        // -- cleared) where gating on a play() promise would not: on WebKit a play() issued at
        // -- readyState 0 can stay pending forever and would wedge this permanently.
        if (audio_play_timer != null){
            if (!restart)
                return;
            clearAudioPlayRetry();
        }

        audio_play_attempts = 0;
        tryAudioPlay(reason);
    }

    function tryAudioPlay(reason){

        if (!audio || !audio.srcObject || !audio.paused)
            return;

        audio.play().catch(e => {
            // -- AbortError just means a newer detach/attach raced this play(); that attach
            // -- owns the element now, so retrying here would fight it
            if (e?.name === "AbortError")
                return;

            if (++audio_play_attempts >= 3){
                if (++audio_play_failures <= 3)
                    Logger.log("Tile", "warn", "Tile Audio Play Failed", {peer_id: peer?.id, reason: reason, error: e?.name ?? String(e), element_muted: audio?.muted});
                return;
            }

            // -- clear before arming: this is the only writer of the handle besides
            // -- clearAudioPlayRetry, and overwriting a live handle would orphan that timer
            // -- where nothing could cancel it
            clearAudioPlayRetry();
            audio_play_timer = setTimeout(() => {
                audio_play_timer = null;
                tryAudioPlay(reason);
            }, 1000);
        });
    }

    function clearAudioPlayRetry(){
        if (audio_play_timer != null){
            clearTimeout(audio_play_timer);
            audio_play_timer = null;
        }
    }

    // -- a swallowed play() rejection on the pump element means the tile silently never renders.
    // -- How to read a NotAllowedError: WebKit short-circuits play() for a MediaStream element,
    // -- with no gesture at all, while the document is capturing (or while anything on the page
    // -- is already audible), ahead of every gesture restriction -- so the rejection rules out
    // -- live capture: gUM never succeeded, or every capture track has been stopped. It does NOT
    // -- mean an interruption; WebKit mutes a capture source rather than dropping it, and a muted
    // -- capture device still counts as capturing. Nor is it proof on its own -- element
    // -- suspended, page media suspended, and the inline-playback check (any MediaStream <video>
    // -- in an iOS WebView left at allowsInlineMediaPlayback=false, i.e. the zapp) are tested
    // -- earlier and reject the same way. For this muted, audio-less element the only gesture
    // -- restrictions that can bite are Low Power Mode and thermal.
    // -- Capped: a pump restart loop must not flood the lambda.
    let pump_play_failures = 0;

    function logPumpPlayFailure(e){

        if (e?.name === "AbortError")
            return;

        if (++pump_play_failures > 3)
            return;

        Logger.log("Tile", "warn", "Tile Video Play Failed", {peer_id: peer?.id, is_local: is_local_tile, error: e?.name ?? String(e), message: e?.message?.slice?.(0, 120)});
    }

    function startVideoProcessor(track) {

        stopVideoProcessor();

        if (get(video_suspended)) return;

        if (!track || isUserMuted(track) || isTrackEnded(track) || !canvas) return;

        const mediaTrack = getMediaStreamTrack(track);
        if (!mediaTrack) return;

        offscreenVideo = document.createElement('video');
        offscreenVideo.srcObject = new MediaStream([mediaTrack]);
        offscreenVideo.muted = true;
        offscreenVideo.playsInline = true;
       
        offscreenVideo.play().catch(e => logPumpPlayFailure(e));

        // -- only draw new frames
        let first_fc = null, counter_live = false;
        let last_drawn_fc = NaN, last_drawn_w = 0, last_drawn_h = 0;

        function pump() {

            rafId = requestAnimationFrame(pump);

            if (!canvasCtx || !offscreenVideo || offscreenVideo.readyState < 2) return;
            if (canvas.width <= 0 || canvas.height <= 0) return;

            const fc = offscreenVideo.getVideoPlaybackQuality?.().totalVideoFrames
                ?? offscreenVideo.webkitDecodedFrameCount ?? NaN;
            if (first_fc === null) first_fc = fc;
            else if (fc !== first_fc) counter_live = true;

            // -- redraw despite an unchanged counter when the canvas was resized: resizing
            // -- the backing store clears it
            if (counter_live && fc === last_drawn_fc
                && canvas.width === last_drawn_w && canvas.height === last_drawn_h)
                return;

            const vw = offscreenVideo.videoWidth;
            const vh = offscreenVideo.videoHeight;
            if (!vw || !vh) return;

            const cAr = canvas.width / canvas.height;
            const fAr = vw / vh;
            let sx = 0, sy = 0, sw = vw, sh = vh;

            if (fAr > cAr) {
                sw = vh * cAr;
                sx = (vw - sw) / 2;
            } else {
                sh = vw / cAr;
                sy = (vh - sh) / 2;
            }

            canvasCtx.drawImage(offscreenVideo, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            last_drawn_fc = fc;
            last_drawn_w = canvas.width;
            last_drawn_h = canvas.height;
        }

        rafId = requestAnimationFrame(pump);
    }

    function stopVideoProcessor() {
        if (rafId != null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
        if (offscreenVideo) {
            offscreenVideo.pause();
            offscreenVideo.srcObject = null;
            offscreenVideo = null;
        }
        if (canvasCtx && canvas) {
            canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    const unsub_name = peer.name.subscribe(n => {
        name = n;
    })

    let subscribed_remote_jid = null;
    let local_unsubs = [];
    let remote_unsubs = [];

    // -- last logged binding state (a jid when bound, an unbound reason otherwise) so that
    // -- repeated configure passes (every peers_version bump) only log transitions
    let tile_bind_logged = null;

    // -- an unbound tile is usually a benign startup race (pl-ws delivers the peer before the
    // -- jitsi participant map catches up, then it binds within a second) -- it was ~75% of all
    // -- warn volume. The warn now only ships if the tile is STILL unbound after the grace
    // -- period; cancelled on bind success, reason change, and unmount.
    const UNBOUND_GRACE_MS = 4000;
    let unbound_timer = null;

    function cancelUnboundTimer(){
        if (unbound_timer != null){
            clearTimeout(unbound_timer);
            unbound_timer = null;
        }
    }

    function logTileUnbound(reason){
        if (tile_bind_logged === reason) return;
        tile_bind_logged = reason;
        cancelUnboundTimer();
        unbound_timer = setTimeout(() => {
            unbound_timer = null;
            // -- a bind since arming rewrites tile_bind_logged to the jid; stay silent then
            if (tile_bind_logged !== reason) return;
            Logger.log("Tile", "warn", "Tile Unbound", {peer_id: peer?.id, reason: reason, waited_ms: UNBOUND_GRACE_MS});
        }, UNBOUND_GRACE_MS);
    }

    let unsub_speaker = null;
    let unsub_conference_peers = () => {};

    function onNameInput(e){
        Peers_Handler.notify_name_change(e.target.value);
    }

    function toggleVideoTrackAttach(track){

        currentVideoTrack = track;
        if (!canvas || !track) {
            stopVideoProcessor();
            return;
        }
        
        if (isUserMuted(track)) {
            stopVideoProcessor();
        } else {
            startVideoProcessor(track);
        }
    }

    // -- The two media-lifecycle levels (MediaLifecycle). Both are pure edge handlers: the stores
    // -- only notify on a real transition, and every other tile input (track swap, user mute)
    // -- funnels through the same two primitives below.

    // -- video: the page went away or the audio session was taken (iOS). Drop the camera pump on
    // -- the way in, rebuild it on the way out honoring the current track + mute state. No-op
    // -- until mounted (canvasCtx set) and for talent tiles (no canvas pump -- their DTS canvas is
    // -- painted by the worker RenderHost).
    $: applyVideoSuspend($video_suspended);

    function applyVideoSuspend(suspended){
        if (!canvasCtx) return;
        suspended ? stopVideoProcessor() : toggleVideoTrackAttach(currentVideoTrack);
    }

    // -- audio: iOS pauses every media element when it takes the audio session and only un-pauses
    // -- them if it supplies the ShouldResume hint, so the remote voice needs re-playing by hand.
    // -- Nothing to do on the way in -- the element is already paused, by the OS.
    $: applyAudioSuspend($audio_suspended);

    function applyAudioSuspend(suspended){

        if (suspended){
            // -- a retry chain can only keep failing while the session is gone; the resume below
            // -- starts a fresh one with a full budget
            clearAudioPlayRetry();
            return;
        }

        // -- restart=true: the interruption invalidated whatever a sleeping chain was waiting on,
        // -- and this is a fresh, user-visible attempt that should get its own budget. Safe before
        // -- onMount (the store's initial false runs this once): it bails on the missing element.
        ensureAudioPlaying("resume", true);
    }

    function toggleAudioTrackAttach(track){

        if (!audio)
            return;

        if (!track){
            clearAudioPlayRetry();
            detachAudioTrack(currentAudioTrack);
            currentAudioTrack = null;
            audio.srcObject = null;
            return;
        }

        // -- the local mute comes only from the selected-device store: a raw track's .muted is the
        // -- source state (jitsi mutes audio with enabled=false), so reading it here dropped the badge
        if (!is_local_tile) mic_muted = isTrackMuted(track);

        if (currentAudioTrack != track){

            // -- a swap reassigns srcObject, which pauses the element and aborts any play() in
            // -- flight. Drop the old track's sleeping retry with it: left armed, it would block
            // -- the new track's own ensureAudioPlaying below and then fire against the new
            // -- element state carrying the old chain's spent attempts and stale reason.
            clearAudioPlayRetry();

            detachAudioTrack(currentAudioTrack);
            attachAudioTrack(track);
            currentAudioTrack = track;

            // -- the endpoint of the whole remote-audio chain: this peer's audio now feeds
            // -- the <audio> element. element_muted=true here should only mean pre-Join.
            if (!is_local_tile)
                Logger.log("Tile", "info", "Tile Audio Attached", {peer_id: peer?.id, track_muted: mic_muted, element_muted: audio_muted});
        }

        // -- re-assert in case attach cleared it; the muted={audio_muted} binding keeps it in
        // -- sync afterwards (e.g. when has_joined flips on first Join).
        audio.muted = audio_muted;

        // -- don't leave the first start to the autoplay attribute (see ensureAudioPlaying)
        ensureAudioPlaying("attach");
    }

    async function setTileOutputDevice(id){
        if (id === currentSinkId)
            return;

        if (audio && BrowserHandler.supports_setSpeakerId(OutputConfig.session.browser)){
            await audio.setSinkId(id);
            currentSinkId = id;
            console.log(`[PL-Tile] Tile ${peer.id} set sink to: ${id}`);
        }
    }

    function controlsRendered(value){
        controls_rendered = value;
    }

    function configureTrackSubscriptions() {
        if (!usesMediaStreamTracks()) {
            unsubscribeLocalTrackSubscriptions();
            unsubscribeRemoteTrackSubscriptions();
            unsubscribeSpeakerSubscription();
            clearAttachedTracks();
            return;
        }

        subscribeSpeakerSelection();

        if (is_local_tile) {
            unsubscribeRemoteTrackSubscriptions();
            subscribeLocalTrackSubscriptions();
            return;
        }

        unsubscribeLocalTrackSubscriptions();
        subscribeRemoteTrackSubscriptions();
    }

    function usesMediaStreamTracks() {
        return peer?.type !== "talent";
    }

    function unsubscribeAll(unsubs) {
        for (const unsub of unsubs)
            unsub();
        unsubs.length = 0;
    }

    function subscribeLocalTrackSubscriptions() {
        if (local_unsubs.length > 0)
            return;

        local_unsubs = [
            subscribeSelectedDeviceTrack("microphone", track => {
                toggleAudioTrackAttach(track);
            }),
            subscribeSelectedDeviceTrack("camera", track => {
                toggleVideoTrackAttach(track);
            }),
            subscribeSelectedDeviceMuted("microphone", muted => {
                mic_muted = muted;
            }),
            subscribeSelectedDeviceMuted("camera", muted => {
                if (muted) {
                    stopVideoProcessor();
                    return;
                }

                toggleVideoTrackAttach(getSelectedDeviceTrack("camera"));
            })
        ];
    }

    function unsubscribeLocalTrackSubscriptions() {
        unsubscribeAll(local_unsubs);
    }

    function subscribeRemoteTrackSubscriptions() {

        // -- bound by pl-ws id: the ConferencePeer appears (and gets its pl_id stamped) when the
        // -- participant's join presence arrives, in whichever order that races the pl-ws peer list
        const conferencePeer = Conference.findByPlId(peer?.id);
        if (!conferencePeer) {
            logTileUnbound("no-conference-peer");
            unsubscribeRemoteTrackSubscriptions();
            clearAttachedTracks();
            return;
        }

        // -- a rejoin surfaces as the same pl_id under a fresh jid: fall through and re-bind
        const jid = conferencePeer.jid;
        if (subscribed_remote_jid === jid)
            return;

        unsubscribeRemoteTrackSubscriptions();

        subscribed_remote_jid = jid;

        if (tile_bind_logged !== jid){
            tile_bind_logged = jid;
            cancelUnboundTimer();
            Logger.log("Tile", "info", "Tile Bound", {peer_id: peer.id, jid: jid});
        }
        remote_unsubs = [
            conferencePeer.subscribeTrack("audio", track => {
                toggleAudioTrackAttach(track);
            }) ?? (() => {}),
            conferencePeer.subscribeTrack("video", track => {
                toggleVideoTrackAttach(track);
            }) ?? (() => {}),
            conferencePeer.subscribeMute("audio", muted => {
                mic_muted = muted;
            }) ?? (() => {}),
            conferencePeer.subscribeMute("video", muted => {
                const videoTrack = get(conferencePeer.tracks.video.track);
                if (muted) {
                    stopVideoProcessor();
                    return;
                }

                toggleVideoTrackAttach(videoTrack);
            }) ?? (() => {})
        ];
    }

    function unsubscribeRemoteTrackSubscriptions() {
        if (!subscribed_remote_jid)
            return;

        unsubscribeAll(remote_unsubs);
        subscribed_remote_jid = null;
    }

    function subscribeSpeakerSelection() {
        if (unsub_speaker)
            return;

        unsub_speaker = subscribeSelectedDeviceSelection("speaker", selection => {
            if (selection?.id) {
                setTileOutputDevice(selection.id);
            }
        });
    }

    function unsubscribeSpeakerSubscription() {
        if (!unsub_speaker)
            return;

        unsub_speaker();
        unsub_speaker = null;
    }

    function clearAttachedTracks() {
        toggleVideoTrackAttach(null);
        toggleAudioTrackAttach(null);
    }

    onMount(async () => {

        // -- Create canvas helpers for jitsi video tiles
        if (canvas){
            canvasCtx = canvas.getContext('2d');
            resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                	// -- can we abstract this into a re usable function since we will have canvases everywhere
                	// -- size the backing store in device pixels (see PlaybackCanvas.svelte)
                    const dpx = entry.devicePixelContentBoxSize?.[0];
                    if (dpx && dpx.inlineSize > 0 && dpx.blockSize > 0) {
                        canvas.width = dpx.inlineSize;
                        canvas.height = dpx.blockSize;
                        continue;
                    }
                    const { width, height } = entry.contentRect;
                    if (width > 0 && height > 0) {
                        const dpr = window.devicePixelRatio || 1;
                        canvas.width = Math.round(width * dpr);
                        canvas.height = Math.round(height * dpr);
                    }
                }
            });

            try { resizeObserver.observe(canvas, { box: 'device-pixel-content-box' }); }
            catch { resizeObserver.observe(canvas); }
        }

        name = get(peer.name);

        // -- not sure we need this --
        if (is_local_tile) Peers_Handler.self_peer.has_joined_session = true;

        // -- Talent peers: transfer canvas to decoder thread for DTS rendering
        if (peer.type === "talent") {
            console.assert(dts_canvas);
            Dispatcher.transferDtsCanvas(peer.id, dts_canvas);
        }
        
        else {

        	// -- this is the new peers listener
            unsub_conference_peers = Conference.peers_version.subscribe(() => {
                configureTrackSubscriptions();
            });

            configureTrackSubscriptions();
            
            if (is_local_tile){
                toggleVideoTrackAttach(getSelectedDeviceTrack("camera"));
                toggleAudioTrackAttach(getSelectedDeviceTrack("microphone"));
            }
            
            const sink_id = getSelectedDevice("speaker")?.id;
            console.log("[PL-TILE] Setting sink id: ", sink_id);

            if (sink_id) await setTileOutputDevice(sink_id);
        }

    })


    onDestroy(() => {

        stopVideoProcessor();
        clearAudioPlayRetry();  // -- an unmounted tile must not keep retrying play() on a dead element
        cancelUnboundTimer();   // -- a destroyed tile (peer left) must not fire a late unbound warn
        resizeObserver && resizeObserver.disconnect();
        unsubscribeLocalTrackSubscriptions();
        unsubscribeRemoteTrackSubscriptions();
        unsubscribeSpeakerSubscription();
        unsub_conference_peers();
        unsub_name();
    })

</script>

<div class="tile-root" class:with-controls={controls_rendered}>

    <div class="tile">

        <audio autoplay muted={audio_muted} bind:this={audio} />

        {#if peer.type === "talent"}
            <canvas bind:this={dts_canvas} class="videointile dts-canvas" class:mirrored={is_local_tile} />
        {:else}
            <canvas
                bind:this={canvas}
                class="videointile"
                class:mirrored={is_local_tile}
            />
        {/if}

        <!-- Absolute Positioning For these -- -->
        <div class="name">
            <input
                spellcheck='false'
                on:input={onNameInput}
                autocomplete="off"
                value={name}
                class='semi-title'
                style="width: calc({Math.max(2, name.length + 0.7)}ch)"
                readonly={!is_local_tile}
            />
        </div>

        {#if mic_muted}
            <i class="fa-thin fa-microphone-slash"></i>
        {/if}
    </div>

    {#if !is_local_tile}
        <PeerControls peer={peer} controlsRendered={controlsRendered}/>
    {/if}

</div>


  <style lang="scss">

    @import "../styles/main.scss";

    .tile-root {

        display: grid;

        grid-template-columns: 1fr;
        gap: 3px;

        border-radius: 10px;
        padding-left: 5px;
        padding-right: 5px;
        padding-bottom: 8px;
        padding-top: 8px;

    }

    // -- expand the tile to make room for level slider and (if applicable) routing controls
    .tile-root.with-controls {
        grid-template-columns: 1fr 48px;
        border: 1px solid #be9d863b
    }

    .tile {

        display: flex;
        flex-direction: row;

        grid-column: 1;
        width: auto;

        aspect-ratio: 1/1;
        max-height: 150px;

        border-radius: $rounded-md;
        position: relative;

        @include narrow {
            max-height: 90px;
        }

    }


    .videointile {

        width: 100%;
        height: 100%;
        border-radius: 6px;
        z-index: -100;

        /* no CSS filter on a video surface: each one is a full-size render pass per frame,
           and some kinds pull the whole window off CoreAnimation delegation */
        transition: opacity 0.3s ease;
        opacity: 1;
        /* -- no explicit visibility here: it must inherit so the App-level
           -- #session-wrapper.covered gate can hide tiles with the session */

        background: #1d1d1d6b;

    }

    .videointile.mirrored {
        transform: scaleX(-1);
    }

    .dts-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .name {

        position: absolute;
        bottom: $space-sm;

        margin-left: $space-sm;
        margin-right: $space-sm;

        border-radius: 16px;
        padding: $space-sm $space-md;
        background: hsla(0, 0%, 0%, 0.32);

        width: fit-content;
        max-width: calc(100% - 2 * $space-sm);

      .semi-title {

        color: $light-color;
        font-size: $font-xsm;
        letter-spacing: 1px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        max-width: 100%;
        min-width: 2ch;
      }
    }
    .fa-microphone-slash{
        position: absolute;
        top: 8px;
        left: 8px;
        color: rgba(254, 3, 3);
        font-size: 2em;
    }


  </style>
